// Brookline Hazard Mitigation Plan 2023
// Source: Town of Brookline, MA Hazard Mitigation Plan 2023 (June 2023; FEMA
//   Approved 11-28-2023). This page presents the plan's Mitigation Action Plan
//   (Chapter 6) — the 59 mitigation actions — organized by the plan's five
//   mitigation goals (per Table 79, Actions Sorted by Goal Statement and Priority),
//   with each action's priority, hazards, lead, supporting agencies, cost, funding,
//   and implementation schedule drawn near-verbatim from Table 74. The risk
//   assessment, capability assessment, and appendices are in the source document.
// Page created August 2026.

export const hazardMitigationData = {
  planYear: '2023',
  updatedDate: 'August 2026',
  introduction: {
    overview: [
      'The Town of Brookline Hazard Mitigation Plan 2023 (FEMA-approved November 28, 2023) identifies the natural hazards and climate-change risks facing Brookline and lays out a plan of mitigation actions to reduce them. Mitigation actions include both policies (such as land-use regulations) and projects (such as infrastructure improvements).',
      'This page organizes the plan’s 59 mitigation actions under the five mitigation goals they advance. Each action carries a priority — assigned by weighing costs against benefits across eight criteria (actions scoring highest are High, then Medium, then Low) — along with the hazards it addresses, its action lead and supporting agencies, estimated cost, potential funding, and an implementation schedule. The plan notes that this action list is aspirational and does not guarantee that every action will be implemented within five years.'
    ],
    itemsNote: [
      'Actions are grouped by the goal they primarily advance (per the plan’s Table 79) and numbered as in the plan’s Table 74. Each action shows its priority (High, Medium, or Low) and its implementation schedule — Short Term (roughly 2023–2025), Medium Term (2023–2027), or Long Term (through 2030).',
      'Use the Priority and Implementation Schedule filters to focus on the actions that matter most to you.'
    ]
  },
  legend: {
    priority: [
      { code: 'High', label: 'High priority in the plan’s cost-benefit ranking' },
      { code: 'Medium', label: 'Medium priority in the plan’s cost-benefit ranking' },
      { code: 'Low', label: 'Low priority in the plan’s cost-benefit ranking' }
    ],
    schedule: [
      { code: 'Short Term', label: 'Short Term — beginning roughly 2023–2025' },
      { code: 'Medium Term', label: 'Medium Term — beginning roughly 2023–2027' },
      { code: 'Long Term', label: 'Long Term — extending through 2030' }
    ]
  },
  goals: [
    {
      id: 'life-property',
      number: 1,
      name: 'Life / Property',
      statement: 'Reduce the loss of life, property, infrastructure, and cultural and historic resources from natural disasters and climate change.',
      actions: [
        {
          num: '2', priority: 'High', title: 'Ensure the resiliency of facilities commonly used for public sheltering, warming, and cooling in severe weather, and accessibility for vulnerable populations',
          description: 'Review and provide recommendations for back-up power — robust enough to power HVAC systems through an extended power outage — for facilities commonly used for emergency sheltering, warming, and cooling centers for residents (Sr. Center; Libraries; Schools). Consider developing relationships with large businesses and institutions to explore opportunities to add informal cooling areas throughout town.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'Emergency Management Coordinator',
          supporting: 'Building; Council on Aging; Libraries; Schools; Public Health',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'DHS UASI; FEMA BRIC',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '4', priority: 'High', title: 'A/C and Vulnerable Populations',
          description: 'Develop strategies to identify and support vulnerable households most in need of air conditioning. Encourage use of efficient air conditioning.',
          hazards: 'Extreme Temperatures',
          lead: 'Public Health Director',
          supporting: 'PH Emergency Preparedness Coordinator; OEM; COA; ODICR',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Mass Save; staff time',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '12', priority: 'High', title: 'Pet Emergency Preparedness',
          description: 'Develop an emergency preparedness plan for pet companions to reduce the number of individuals sheltering in place during extreme weather events.',
          hazards: 'Severe Winter Storms, Extreme Temperatures',
          lead: 'Public Health Emergency Preparedness Coordinator',
          supporting: 'Office of Emergency Management',
          cost: 'Low (< $50,000)',
          funding: 'Town general operating funds / Staff time',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '21', priority: 'Medium', title: 'Households reliant on electricity for medical devices',
          description: 'Identify households reliant on electricity for medical devices. Develop plans for backup power in the event of outages.',
          hazards: 'Severe Winter Storms, Extreme Temperatures',
          lead: 'Public Health Emergency Preparedness Coordinator',
          supporting: 'Office of Emergency Management',
          cost: 'Low (< $50,000)',
          funding: 'Town general operating funds / Staff time',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '22', priority: 'Medium', title: 'Stabilize location at Rt. 9 (Boylston St.) & Reservoir',
          description: 'Develop and implement recommendations for stabilization of the deteriorating slope behind Fire Station #4, impacting the parking lot used by firefighters.',
          hazards: 'Landslide',
          lead: 'DPW Director of Engineering & Transportation',
          supporting: 'Building Department',
          cost: 'High (> $250,000)',
          funding: 'Town general operating funds / Staff time',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '23', priority: 'Medium', title: 'Assist with development of COOP Plans for local medical facilities',
          description: 'Reach out to hospitals, nursing homes, and group homes to ensure they have Continuity of Operations (COOP) plans so staff can perform essential functions during emergencies.',
          hazards: 'Severe Winter Storms, Extreme Temperatures',
          lead: 'Public Health Emergency Preparedness Coordinator',
          supporting: 'Office of Emergency Management',
          cost: 'Low (< $50,000)',
          funding: 'Town general operating funds / Staff time',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '30', priority: 'Medium', title: 'Hazard Tree Removal: Reduce risk of wildfire',
          description: 'Implement hazard tree removal to reduce the risk of wildfire in Putterham Woods.',
          hazards: 'Wildfires/Brush Fires, Hurricanes/Wind',
          lead: 'DPW Director of Parks and Open Space',
          supporting: 'DPW Tree Warden',
          cost: 'High (> $250,000)',
          funding: 'Town general operating funds / Staff time',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        }
      ]
    },
    {
      id: 'infrastructure',
      number: 2,
      name: 'Infrastructure',
      statement: 'Mitigate risk to critical facilities and infrastructure from natural hazards and climate change.',
      actions: [
        {
          num: '5', priority: 'High', title: 'Electric Microgrid Power Generation Feasibility Study',
          description: 'Study the feasibility of one or more microgrids to replace petroleum generators for emergency backup power for critical facilities (Municipal Service Center / EOC; Town Hall / PSB; facilities used for shelter and warming/cooling centers).',
          hazards: 'Severe Winter Storms, Extreme Temperatures',
          lead: 'Director of Sustainability',
          supporting: 'Building Department, OEM',
          cost: 'High (> $250,000)',
          funding: 'FEMA BRIC',
          schedule: 'Long Term (2023–2030)', scheduleTerm: 'Long Term'
        },
        {
          num: '13', priority: 'High', title: 'Develop and Fund a Strategic Plan for Road Services',
          description: 'The Road Services Strategic Plan lays out system needs, anticipated service levels, and an asset-management approach to road maintenance and improvement. The intent is to use the Transportation and Mobility Plan & Conditions Assessment and overlay it with critical routes and infrastructure, and fund roads to assure better resiliency during and following severe weather events to support emergency services, transport, economy, etc.',
          hazards: 'Severe Winter Storms, Extreme Temperatures',
          lead: 'Director of Engineering and Transportation',
          supporting: 'Office of Emergency Management, Planning',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Town Capital Improvement Program, Technical Assistance Grant',
          schedule: 'Long Term (2025–2030)', scheduleTerm: 'Long Term'
        },
        {
          num: '14', priority: 'High', title: 'Ensure the long-term resiliency of public safety facilities',
          description: 'Review and provide recommendations for upgraded back-up power — robust enough to power HVAC systems through an extended power outage — for all facilities dedicated to public safety. In particular, the Fire Training / Maintenance building has no back-up power; most fire stations (except #6) need upgraded back-up systems.',
          hazards: 'Severe Winter Storms, Extreme Temperatures',
          lead: 'Fire Chief',
          supporting: 'Building Department, OEM',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'FEMA EMPG; DHS UASI',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '31', priority: 'Medium', title: 'Wastewater Infiltration & Inflow (I&I)',
          description: 'Continue to implement the Town’s goal of lining 100% of the sewer system to reduce infiltration & inflow.',
          hazards: 'Flood',
          lead: 'DPW Director of Water & Sewer',
          supporting: 'Public Works',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Town general operating budget',
          schedule: 'Long Term (2023–2030)', scheduleTerm: 'Long Term'
        },
        {
          num: '44', priority: 'Medium', title: 'Wastewater & Stormwater Infrastructure Protection',
          description: 'Review and provide recommendations to protect critical drainage infrastructure crossings located under other utilities and vital transportation networks. Critical sewer & drainage crossings are located in areas like Beacon Street, where sewer & stormwater pipes/culverts run underneath MWRA 48" water transmission lines and the MBTA C line (and pipes run underneath the D line too). One section of the Tannery Brook Drain is located under four 48" MWRA lines and the C line; it is stone-lined and requires a structural liner. The Brookline Main sewer is large with significant flows, constructed of brick/concrete, and a portion of it is located in the ROW for the MBTA D line. Other crossings may only require an assessment and rating to determine if structural liners are required.',
          hazards: 'Flood, Climate Change',
          lead: 'DPW Director of Engineering and Transportation, DPW Director of Water and Sewer',
          supporting: 'Public Works',
          cost: 'High (> $250,000)',
          funding: 'Town Capital Improvement Plan, MWRA Loan/Zero Interest and Grant Program, Federal Infrastructure Bond Bill, FEMA FMA',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '45', priority: 'Medium', title: 'Relocation of Water and Sewer Division Facility',
          description: 'The DPW Water and Sewer Division is one of the primary response organizations for flood mitigation; however, the facility is located within a flood plain and at risk of impact as the severity of flooding increases in the future. Recommendation to complete a feasibility study to relocate the Water and Sewer Division out of the flood plain and to consolidate and expand at the Municipal Service Center/Transfer Station.',
          hazards: 'Flood',
          lead: 'Public Works Commissioner; Director of Public Buildings',
          supporting: 'Building Dept.',
          cost: 'High (> $250,000)',
          funding: 'BRIC; Flood Mitigation',
          schedule: 'Long Term (2023–2030)', scheduleTerm: 'Long Term'
        },
        {
          num: '59', priority: 'Low', title: 'Earthquake Infrastructure Assessment',
          description: 'Conduct an earthquake infrastructure assessment of the vulnerability of roadways and utilities in high liquefaction susceptibility areas, and identify retrofits if needed to mitigate earthquake hazards.',
          hazards: 'Earthquake',
          lead: 'DPW Director of Engineering & Transportation',
          supporting: 'Public Works',
          cost: 'High (> $250,000)',
          funding: 'FEMA BRIC; Infrastructure Bond Bill; Other Grants',
          schedule: 'Long Term (2025–2030)', scheduleTerm: 'Long Term'
        }
      ]
    },
    {
      id: 'capacity',
      number: 3,
      name: 'Capacity',
      statement: 'Expand the Town’s capacity to mitigate risk by adopting a culture of hazard mitigation through regulations, planning, and regional collaboration.',
      actions: [
        {
          num: '3', priority: 'High', title: 'Add resiliency and emergency capacity to new school facilities',
          description: 'The Baker School is next on the list of school renovations (likely 2030 timeframe). Last updated in 1999, the campus includes buildings originally built in 1932 and 1950. Consider including the following add-ons for future Town / emergency needs: emergency shelter storage room (approx. 500 sq. ft.); larger-capacity fridge/freezer for Food Pantry / shelter backup; resilient power backup / microgrid; enhanced HVAC system including negative air pressure and/or increased air recirculation.',
          hazards: 'Severe Winter Storms, Extreme Temperatures',
          lead: 'Director of Operations and Facilities, Public Schools of Brookline',
          supporting: 'Office of Emergency Management, Building Department',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Long Term (2025–2030)', scheduleTerm: 'Long Term'
        },
        {
          num: '6', priority: 'High', title: 'Update Stormwater Bylaw and Develop Regulations',
          description: 'Update the Town’s Stormwater bylaw (Article 8.26) to meet MS4 Permit requirements and develop regulations to support the site plan review process, clarify requirements/performance standards, protect the Town’s infrastructure, mitigate flooding, and protect the environment.',
          hazards: 'Flood, Climate Change',
          lead: 'Director of Engineering and Transportation',
          supporting: 'Director of Water and Sewer, Environmental Engineer, Commissioner of Public Works',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Consultant / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '7', priority: 'High', title: 'Create a Flood Hazard Identification Plan',
          description: 'Document the results of current and future flooding predictions from a new Town-wide flood model. Define the Town’s approach to comprehensive floodplain management including land-use planning, flood mitigation efforts, and flood protection facilities/infrastructure (natural/built) and emergency response management. Assess vulnerabilities, define consequences, complete a flood risk assessment, and coordinate recommended actions/interventions with local and regional partners.',
          hazards: 'Flood, Climate Change',
          lead: 'DPW Director of Engineering and Transportation, DPW Director of Water and Sewer',
          supporting: 'DPW Environmental Engineer, Director of Parks and Open Space, DPW Conservation Administrator, Office of Emergency Management',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Staff time / Consultant / Town general operating budget; FEMA Flood Mitigation',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '15', priority: 'High', title: 'Implement Stormwater Solutions in all DPW and Building Department Projects',
          description: 'Implement stormwater management solutions in Town projects, to include grey and green infrastructure to manage the flow of stormwater and increase infiltration on site.',
          hazards: 'Flood, Climate Change',
          lead: 'Director of Engineering and Transportation',
          supporting: 'Director of Water and Sewer, Environmental Engineer, Building Commissioner',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Town operating budget and Capital Investment Plan',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '16', priority: 'High', title: 'Update Sanitary Sewer Regulations',
          description: 'Update regulations that govern the use of public sewers, the installation and connection of building sewers, and the discharge of water and wastes into the public sewer system. The purpose is to protect water supplies, prevent pollution in surface and groundwater, and prevent nuisances and potential health hazards.',
          hazards: 'Flood, Climate Change',
          lead: 'DPW Director of Water & Sewer',
          supporting: 'Public Health',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Consultant / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '17', priority: 'High', title: 'Develop an Electric Vehicle Infrastructure Plan to mitigate risks of power outages from natural hazards',
          description: 'Plan development in process. Consider the resilience of the network; electric vehicle parking (existing & future parking lots); and the recent bicycle bylaw.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'DPW Director of Highway and Sanitation, DPW Fleet Supervisor',
          supporting: 'Building; Sustainability',
          cost: 'Low (< $50,000)',
          funding: 'Town general operating funds / Staff time',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '24', priority: 'Medium', title: 'Incorporate climate adaptation measures into the next Brookline Climate Action Plan',
          description: 'Incorporate climate adaptation measures into the Climate Crisis Action & Resiliency Plan Update. Action items from this Hazard Mitigation Plan 2023 will be incorporated.',
          hazards: 'Severe Winter Storms, Extreme Temperatures, Climate Change',
          lead: 'Director of Sustainability',
          supporting: 'Department of Planning and Community Development; DPW',
          cost: 'Low (< $50,000)',
          funding: 'MVP Grant; staff time',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '25', priority: 'Medium', title: 'Climate Resilience in Site Development',
          description: 'Adopt policies that require or incentivize the use of lighter-colored building materials, green roofs, the dedication of part of a development area as open space, and planting street trees to mitigate extreme temperatures and drought.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'DPW Director of Parks and Open Space, DPW Tree Warden',
          supporting: 'Department of Public Works',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Staff time / Town general operating budget; MAPC ACR grant',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '26', priority: 'Medium', title: 'Update Snow and Ice Winter Management Plan to Include Snow Storage Locations',
          description: 'Update the Brookline Snow and Ice Winter Management Plan and add preparations and actions for extreme conditions, to include planning for temporary one-way routes, snow storage, mobile fuel, hotel accommodations, national guard deployment, equipment resources, bridges/banks/public safety hazards, public transit, and regional coordination.',
          hazards: 'Severe Winter Storms, Climate Change',
          lead: 'DPW Deputy Commissioner of Operations, DPW Commissioner of Public Works',
          supporting: 'Environmental, DPW Health and Safety Officer, Office of Emergency Management',
          cost: 'Low (< $50,000)',
          funding: 'Town general operating funds / Staff time',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '27', priority: 'Medium', title: 'Update the Emergency Action Plan for the Brookline Reservoir Dam',
          description: 'Update the Emergency Action Plan for the Brookline Reservoir Dam, including updated inundation map modeling.',
          hazards: 'Flood, Hurricanes/Wind',
          lead: 'DPW Environmental Engineer',
          supporting: 'DPW Director of Engineering and Transportation, DPW Director of Parks and Open Space',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Consultant / Town general operating budget',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '32', priority: 'Medium', title: 'Commence Stormwater Flow Assessment/Modeling',
          description: 'Based upon flow meters and other available data, create a stormwater management model that can be expanded over time and will assess peak flows, system capacity, and system vulnerabilities. This is the first step towards the updated Stormwater Management Plan.',
          hazards: 'Flood, Climate Change',
          lead: 'Director of Engineering and Transportation, Environmental Engineer',
          supporting: 'Public Works',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Water and Sewer Enterprise Fund, FEMA BRIC',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '33', priority: 'Medium', title: 'Ensure all Town Projects Incorporate Climate Resilience',
          description: 'Develop a checklist, process, and internal review prior to submitting CIP projects that includes consideration of climate resiliency and ways to protect the investment, design thoughtfully, and utilize the site for mitigation.',
          hazards: 'Severe Winter Storms, Extreme Temperatures, Climate Change',
          lead: 'Director of Sustainability; Director of Public Buildings',
          supporting: 'Building Department',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Town general operating funds / Staff time; MAPC ACR grant',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '34', priority: 'Medium', title: 'Infectious Disease planning and response',
          description: 'Revise and edit preparedness and response plans for health threats such as infectious diseases related to climate change.',
          hazards: 'Infectious Diseases',
          lead: 'Public Health Director',
          supporting: 'PH Nurse, PH Emergency Preparedness Coordinator',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Public Health Emergency Preparedness Grant',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '35', priority: 'Medium', title: 'Incorporate stormwater management into Roadway Reconstruction Projects',
          description: 'Incorporate stormwater management techniques into all roadway reconstruction projects to ensure the transportation system can function before, during, and after a natural disaster or event. For example, prioritize mode shift to more sustainable modes through policy initiatives, design, and construction of the Town’s public ways. The Town should also take an advocacy role with the MBTA and MassDOT to protect the highway and transit networks from climate change.',
          hazards: 'Flood, Climate Change',
          lead: 'Director of Engineering and Transportation',
          supporting: 'Public Works, MBTA, DOT',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Town’s Capital Improvement Plan, MassDOT Funding, Federal Infrastructure Bond Bill, FEMA BRIC',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '36', priority: 'Medium', title: 'Adopt development Site Design guidelines to increase tree plantings near buildings, in parking areas, and along public ways',
          description: 'Adopt development Site Design guidelines to increase tree plantings near buildings, increase the percentage of trees used in parking areas, and along public ways to mitigate extreme temperatures.',
          hazards: 'Extreme Temperatures',
          lead: 'Director of Parks and Open Space, Tree Warden',
          supporting: 'Planning / Conservation',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '37', priority: 'Medium', title: 'Adopt development guidelines for drought-tolerant landscaping and site design measures',
          description: 'Adopt development guidelines for drought-tolerant landscaping and site design measures to mitigate drought hazards.',
          hazards: 'Drought',
          lead: 'DPW Director of Parks and Open Space, DPW Tree Warden',
          supporting: 'Planning / Conservation',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '46', priority: 'Medium', title: 'Create a Stormwater and Green Infrastructure Master Plan to replace the 1999 Stormwater Master Plan',
          description: 'Update the Town’s Stormwater Management Plan with emphasis on the environment, flooding, and green infrastructure, along with system improvements, capacity improvements as necessary, regulatory compliance, public education, and maintenance/management practices.',
          hazards: 'Flood, Climate Change',
          lead: 'DPW Director of Engineering & Transportation',
          supporting: 'DPW Director of Water and Sewer, DPW Environmental Engineer, DPW Director of Parks and Open Space',
          cost: 'High (> $250,000)',
          funding: 'FEMA BRIC; Infrastructure Bond Bill; Other Grants',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '47', priority: 'Medium', title: 'Update GIS data layers to include all critical infrastructure, utilities and public assets',
          description: 'Ensure all critical infrastructure, utilities, and public assets are mapped on GIS so the data is available and accurate when needed for an emergency. Use the GIS system to identify high-hazard areas.',
          hazards: 'Severe Winter Storms, Extreme Temperatures, Climate Change',
          lead: 'DPW Systems Manager, IT GIS Manager, DPW Deputy Commissioner of Operations',
          supporting: 'Public Works; IT; OEM',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Town general operating funds / Staff time',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '48', priority: 'Medium', title: 'Planning for resiliency in Building Codes',
          description: 'Brookline has adopted both the new Stretch Code and the Specialized Opt-In Stretch Code promulgated by the Commonwealth under the MA Dept. of Energy Resources (DOER). The codes deal with energy efficiency and energy use in buildings, but not resiliency. The Town should consider including resiliency in the code discussion. Brookline is also participating in the MA DOER Fossil Free Demonstration Project; all new construction and major renovations will be fossil free.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'Director of Sustainability',
          supporting: 'Building; Planning',
          cost: 'Low (< $50,000)',
          funding: 'Town general operating funds / Staff time',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '49', priority: 'Medium', title: 'Develop Comprehensive Damage Assessment Plan',
          description: 'Develop a plan and train staff in basic damage assessment and “windshield” surveys to quickly assess and prioritize damage throughout the town. Create an updated catalog of “before” photos of public assets to assist in documentation of damage for potential reimbursement after a disaster.',
          hazards: 'Severe Winter Storms, Hurricanes/Wind',
          lead: 'Emergency Management Coordinator',
          supporting: 'Building Department, Department of Public Works',
          cost: 'Low (< $50,000)',
          funding: 'Town general operating funds / Staff time',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '50', priority: 'Medium', title: 'Develop Standard Operating Procedures for Routine Emergency Response Actions for Natural Disasters',
          description: 'Develop interagency SOPs to address large- and small-scale emergencies that provide an outline of actions and responsibilities of each organization during a response. Events include downed tree limbs, downed or impacted electrical wires, road closures, safe work zones during an emergency, etc.',
          hazards: 'Severe Winter Storms, Hurricanes/Wind',
          lead: 'DPW Deputy Commissioner of Public Works, DPW Division Directors, Tree Warden',
          supporting: 'OEM, Police, Fire',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Consultant / Town general operating budget',
          schedule: 'Long Term (2023–2030)', scheduleTerm: 'Long Term'
        },
        {
          num: '51', priority: 'Medium', title: 'Update Hazardous Materials Emergency Response Plan',
          description: 'Update Brookline’s Hazardous Materials ERP and add preparations and actions for Hazmat mitigation activities including facility liaisons, commodity transportation, chemical storage, response equipment and training, emergency notifications, extreme weather issues, etc.',
          hazards: 'Hazardous Materials',
          lead: 'Director of Emergency Management',
          supporting: 'OEM, Fire, Public Health, Public Works',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Consultant / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '55', priority: 'Low', title: 'Update Mutual Aid Agreements',
          description: 'Update multi-jurisdictional agreements for resource use and sharing during emergency events.',
          hazards: 'Flood, Hurricanes/Wind',
          lead: 'Commissioner of Public Works, DPW Deputy Commissioner of Operations',
          supporting: 'Public Works, Town Counsel',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '56', priority: 'Low', title: 'Green Infrastructure Standards',
          description: 'Develop design guidelines for Green Infrastructure and Low Impact Development.',
          hazards: 'Flood, Wildfires/Brush Fires',
          lead: 'Director of Engineering and Transportation, Director of Parks and Open Space',
          supporting: 'Planning',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        }
      ]
    },
    {
      id: 'natural-resources',
      number: 4,
      name: 'Natural Resources',
      statement: 'Implement actions that minimize risk from climate change and natural hazards to preserve or restore the functions of natural systems.',
      actions: [
        {
          num: '18', priority: 'High', title: 'Incorporate climate adaptation measures into the next Brookline Open Space Plan',
          description: 'Incorporate climate resilience into open space planning. Strategic considerations include: 1) protecting large and/or connected green spaces to foster resilience and biodiversity; 2) creating green space to cool “hot spots”; 3) maintaining or creating open space buffers to protect water quality and provide flood protection; 4) identifying locations where soils will support stormwater infiltration to replenish groundwater and support stream flow. Consider carbon emission sequestration & stormwater storage.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'DPW Director of Parks and Open Space, DPW Conservation Administrator',
          supporting: 'Public Works',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Consultant / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '19', priority: 'High', title: 'Invasive Plant, Pest and Hazard Communication',
          description: 'Monitor for new invasive plant and animal species that may be introduced by a warming climate. Develop management strategies as needed, including educational materials for species that may be harmful to human health and the environment.',
          hazards: 'Invasive Species',
          lead: 'DPW Director of Parks and Open Space, DPW Tree Warden',
          supporting: 'Public Works',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '38', priority: 'Medium', title: 'Implement Tree Removal and Replacement / Urban Forestry Management to mitigate wind hazards',
          description: 'As trees mature or are impacted by storm damage or disease, it is critical to remove them before they become public safety hazards. New tree plantings are also critical, as they improve stormwater absorption and reduce heat in the summer. New trees will be planted in anticipation of the ultimate loss of existing mature trees. Urban forestry management will help with resiliency to disease and pests. Utilize the tree canopy and heat maps to strategically increase tree planting and landscaping for heat relief.',
          hazards: 'Extreme Temperatures, Hurricanes/Wind',
          lead: 'DPW Director of Parks and Open Space, DPW Tree Warden',
          supporting: 'DPW',
          cost: 'High (> $250,000)',
          funding: 'Town funding through Capital Improvement Program',
          schedule: 'Long Term (2023–2030)', scheduleTerm: 'Long Term'
        },
        {
          num: '39', priority: 'Medium', title: 'Dredge Willow Pond',
          description: 'Remove petroleum contaminants and accumulated sediment from Willow Pond to increase the storage capacity of the pond during wet weather and to restore the ecological value of the pond.',
          hazards: 'Flood, Hazardous Materials',
          lead: 'DPW Director of Engineering & Transportation',
          supporting: 'DPW Director of Parks and Open Space, DPW Conservation Administrator',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Town funding through Capital Improvement Program',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '40', priority: 'Medium', title: 'Implement a Tree Protection Bylaw',
          description: 'Implement a tree protection bylaw to protect trees of a certain size on private property to help sustain the overall benefit of the urban tree canopy in Brookline. Implementation will require staffing and/or consulting services to support.',
          hazards: 'Extreme Temperatures, Flood, Climate Change',
          lead: 'DPW Director of Parks and Open Space, Director of Engineering and Transportation, Tree Warden',
          supporting: 'Public Works',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Staff time / Consultant / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '52', priority: 'Medium', title: 'Leverett Pond Bank Stabilization and Planting',
          description: 'Increase the stability of the Leverett Pond bank and plantings to better manage flood events and increase the capacity of the natural environment to benefit the community.',
          hazards: 'Flood, Climate Change',
          lead: 'DPW Director of Engineering and Transportation, DPW Director of Parks and Open Space, DPW Conservation Administrator',
          supporting: 'Public Works',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Town funding through Capital Improvement Program',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '57', priority: 'Low', title: 'Bank Stabilization and Invasive Vegetation Removal of Riverway Park from Brookline Avenue to Park Drive in Boston',
          description: 'Design bank stabilization of Riverway Park to best manage and convey waters in severe weather events. Remove remaining stands of phragmites to prevent constriction of water flow. Consider pedestrian/bike-scale lighting for use as an important alternate corridor for bike/ped/emergency vehicles.',
          hazards: 'Flood, Invasive Species',
          lead: 'DPW Director of Parks and Open Space, DPW Director of Engineering and Transportation, DPW Conservation Administrator',
          supporting: 'Brookline Public Works, Boston Parks',
          cost: 'High (> $250,000)',
          funding: 'FEMA BRIC, Infrastructure Bond Bill, DCR, Brookline, Boston',
          schedule: 'Long Term (2025–2030)', scheduleTerm: 'Long Term'
        },
        {
          num: '58', priority: 'Low', title: 'Implement public golf course drainage plan',
          description: 'The RTL Public Golf Course is in a flood plain and serves the town as a flood zone to store water for the areas surrounding the course, including local neighborhoods & the Country Club. Implement the course drainage plan, per the new 2023 Landscape Master Plan.',
          hazards: 'Flood, Climate Change',
          lead: 'Recreation Director, Golf Course Manager',
          supporting: 'Public Works',
          cost: 'High (> $250,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Long Term (2025–2030)', scheduleTerm: 'Long Term'
        }
      ]
    },
    {
      id: 'education',
      number: 5,
      name: 'Education',
      statement: 'Educate all stakeholders about the value of hazard mitigation and how to implement it in their work, businesses, and homes.',
      actions: [
        {
          num: '1', priority: 'High', title: 'Emergency Preparedness for Facilities with Vulnerable Populations',
          description: 'Reach out to facilities that serve vulnerable populations. Assess retrofit needs and emergency readiness, including evacuation plans. Review needs for air conditioning and back-up generators. Encourage sign-up for the emergency notification system.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'Emergency Management Coordinator',
          supporting: 'PH Emergency Preparedness Coordinator',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '8', priority: 'High', title: 'Vulnerable Populations Assessment',
          description: 'Conduct an assessment of vulnerable populations. Identify gaps in services and prioritize strategies to address gaps.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'Public Health Director',
          supporting: 'ODICR Assistant Director; Community Relations Specialist',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Staff time / Town general operating budget / ARPA',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '9', priority: 'High', title: 'Linguistically Isolated Households',
          description: 'Create a plan to conduct outreach to linguistically isolated households. Provide emergency preparedness information in their native languages. Consider needs for translator services.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'Public Health Director',
          supporting: 'ODICR Language Access Coordinator; OEM',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '10', priority: 'High', title: 'Climate Change & Health',
          description: 'Outreach campaign to communicate the health-related aspects of climate change.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'Public Health Director',
          supporting: 'Public Health, OEM',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '11', priority: 'High', title: 'Extreme Heat Campaign',
          description: 'Create an outreach campaign focused on the impacts of extreme heat and how to manage it. Partner with local hospitals and social assistance organizations on awareness campaigns for heat-related illnesses.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'Public Health Director',
          supporting: 'Brookline Housing Authority, Food Pantry, OEM',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '20', priority: 'High', title: 'Vulnerable Population Outreach',
          description: 'Review Town strengths and weaknesses regarding outreach and connections to vulnerable populations. Identify community partners that can strengthen relationships where needed.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'Public Health Director',
          supporting: 'ODICR Community Engagement Specialist; OEM',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Staff time / Town general operating budget / ARPA',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '28', priority: 'Medium', title: 'Extreme weather guidance for town employees',
          description: 'Develop extreme weather guidance for town employees whose jobs involve outdoor work.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'DPW Commissioner of Public Works, DPW Environmental, Health and Safety Officer',
          supporting: 'Public Health',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '29', priority: 'Medium', title: 'Increase multilingual education and awareness in the community of environmental hazards caused by climate change and ways to address them',
          description: 'Develop tools for education, outreach and engagement.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'Public Health Director; PH Emergency Preparedness Coordinator',
          supporting: 'Planning and Community Development, Public Works',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '41', priority: 'Medium', title: 'Contractor/Homeowner Educational Material',
          description: 'Develop and distribute education and outreach materials on climate-related retrofits including elevating utilities, preventing backflow, protecting basements, and weatherization. Create a contractor/homeowner resilience checklist to address flood and heat strategies, and a checklist for use in inspections.',
          hazards: 'Extreme Temperatures, Flood, Climate Change',
          lead: 'Public Health Director',
          supporting: 'Planning, Building, Public Works',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '42', priority: 'Medium', title: 'Landscape Guidance',
          description: 'Provide landscaping guidance for the use of native plant species and drought-tolerant plants.',
          hazards: 'Drought, Invasive Species',
          lead: 'DPW Director of Parks and Open Space',
          supporting: 'Public Works',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        },
        {
          num: '43', priority: 'Medium', title: 'Workplace safety issues',
          description: 'Promote public awareness and use of educational materials that address workplace safety issues for extreme heat/cold.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'Public Health Director',
          supporting: 'PH Emergency Preparedness Coordinator, DPW Environmental Safety Officer',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '53', priority: 'Low', title: 'Climate change related data tracking',
          description: 'Implement data tracking of environmental conditions, disease risks, and disease occurrence related to climate change.',
          hazards: 'Infectious Disease, Climate Change',
          lead: 'Public Health Director',
          supporting: 'Public Health',
          cost: 'Medium ($50,000 – $250,000)',
          funding: 'Public Health Excellence Grant; Staff time / Town general operating budget',
          schedule: 'Short Term (2023–2025)', scheduleTerm: 'Short Term'
        },
        {
          num: '54', priority: 'Low', title: 'Green Infrastructure Training',
          description: 'Provide training, as needed, for Town staff implementing and maintaining new green infrastructure strategies.',
          hazards: 'Extreme Temperatures, Climate Change',
          lead: 'DPW Director of Parks and Open Space',
          supporting: 'DPW Director of Engineering and Transportation, DPW Director of Water and Sewer',
          cost: 'Low (< $50,000)',
          funding: 'Staff time / Town general operating budget',
          schedule: 'Medium Term (2023–2027)', scheduleTerm: 'Medium Term'
        }
      ]
    }
  ]
};
